import React, { useState } from 'react';
import { formatAmount, getStatusColor, formatPercentage } from './utils/formatters';
import type { TransactionSummary } from './types';
import { ChevronDown, ChevronRight, TrendingUp, DollarSign, Activity, Calendar } from 'lucide-react';

interface TransactionSummaryProps {
  stats: {
    totalTransactions: number;
    totalAmount: number;
    totalSiteFees?: number;
    totalBankFees?: number;
    totalResellerFees?: number;
    approvedTransactions: number;
    declinedTransactions: number;
    returnedTransactions: number;
  };
  dateRange: string;
  queryDateRange?: {
    start: Date;
    end: Date;
  };
}

export function TransactionSummary({ stats, dateRange, queryDateRange }: TransactionSummaryProps) {
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({});

  const totalSiteFees = stats.totalSiteFees || 0;
  const totalBankFees = stats.totalBankFees || 0;
  const totalResellerFees = stats.totalResellerFees || 0;

  const toggleSection = (section: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  return (
    <div className="p-6">
      <h2 className="section-title mb-4">Transaction Summary</h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {stats ? (
          <>
            <div className="stat-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Activity className="w-4 h-4 text-slate-600" />
                </div>
                <span className="stat-label">Total Transactions</span>
              </div>
              <p className="stat-value">{stats.totalTransactions.toLocaleString()}</p>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-violet-50 flex items-center justify-center">
                  <DollarSign className="w-4 h-4 text-violet-600" />
                </div>
                <span className="stat-label">Transaction Volume</span>
              </div>
              <p className="stat-value">{formatAmount(stats.totalAmount)}</p>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-emerald-50 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-emerald-600" />
                </div>
                <span className="stat-label">Net Revenue</span>
              </div>
              <p className="text-2xl font-bold text-emerald-600 mt-1.5">
                {formatAmount(totalSiteFees - totalBankFees - totalResellerFees)}
              </p>
              <div className="mt-3 space-y-1.5 text-[13px]">
                <div className="flex justify-between text-slate-500">
                  <span>Site Fees</span>
                  <span className="font-medium text-slate-700">{formatAmount(totalSiteFees)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Bank Fees</span>
                  <span className="font-medium text-slate-700">-{formatAmount(totalBankFees)}</span>
                </div>
                <div className="flex justify-between text-slate-500">
                  <span>Reseller Fees</span>
                  <span className="font-medium text-slate-700">-{formatAmount(totalResellerFees)}</span>
                </div>
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Activity className="w-4 h-4 text-slate-600" />
                </div>
                <span className="stat-label">Status Breakdown</span>
              </div>
              <div className="space-y-2 mt-1">
                {[
                  { status: 'approved', count: stats.approvedTransactions },
                  { status: 'declined', count: stats.declinedTransactions },
                  { status: 'returned', count: stats.returnedTransactions }
                ].map(({ status, count }) => (
                  <div key={status} className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <span className={`w-2 h-2 rounded-full ${getStatusColor(status)}`} />
                      <span className="text-[13px] font-medium text-slate-700 capitalize">{status}</span>
                    </div>
                    <div className="text-right">
                      <span className="text-[13px] font-semibold text-slate-900">
                        {count.toLocaleString()}
                      </span>
                      <span className="text-[11px] text-slate-400 ml-1">
                        ({((count / stats.totalTransactions) * 100).toFixed(1)}%)
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="stat-card">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-8 h-8 rounded-lg bg-slate-100 flex items-center justify-center">
                  <Calendar className="w-4 h-4 text-slate-600" />
                </div>
                <span className="stat-label">Date Range</span>
              </div>
              <p className="text-lg font-semibold text-slate-900 mt-1">
                {dateRange === '3days' ? 'Last 3 Days' :
                 dateRange === '1month' ? 'Last Full Month' :
                 'Custom Range'}
              </p>
              {queryDateRange && (
                <p className="mt-1 text-[13px] text-slate-500">
                  {queryDateRange.start.toLocaleDateString()} - {queryDateRange.end.toLocaleDateString()}
                </p>
              )}
            </div>
          </>
        ) : (
          <div className="col-span-4 text-center py-8">
            <p className="text-[14px] text-slate-500">No transaction data available</p>
          </div>
        )}
      </div>
    </div>
  );
}
